import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ip_back } from "../config";
import AgentMark from "../digiping/AgentMark";
import ScforhWhoCapsule from "./ScforhWhoCapsule";
import { isScforhSeedComplete } from "../constants/scforhClubHealth";
import { FlttFormDialog } from "./newcomer/FlttFormDialog";
import "./ParentProfilePanel.css";

const getApiUrl = (endpoint) => {
  const base = (ip_back || "").toString().replace(/\/+$/, "");
  const path = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
  return `${base}${path}`;
};

function resolveChildImageUrl(url) {
  if (!url) return null;
  const raw = String(url).trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw) || raw.startsWith("data:") || raw.startsWith("blob:")) {
    return raw;
  }
  const base = (ip_back || "").toString().replace(/\/+$/, "");
  return `${base}${raw.startsWith("/") ? raw : `/${raw}`}`;
}

/**
 * Parent-facing profile panel: club health note, OMS capsule, linked children,
 * and open-player-account for age-eligible kids.
 */
export default function ParentProfilePanel({ user, scforhSeed, onRelationshipDetected, flttClub = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openingId, setOpeningId] = useState(null);
  const [photoBusyId, setPhotoBusyId] = useState(null);
  const [consentBusyKey, setConsentBusyKey] = useState(null);
  const [medicalDraft, setMedicalDraft] = useState({});
  const [medicalBusyId, setMedicalBusyId] = useState(null);
  const [emailDraft, setEmailDraft] = useState({});
  const [feedback, setFeedback] = useState(null);
  const [flttChild, setFlttChild] = useState(null);
  const photoInputRefs = useRef({});

  const loadChildren = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(getApiUrl("/api/parent/children"), { headers });
      const list = Array.isArray(res.data?.children) ? res.data.children : [];
      setChildren(list);
      setMedicalDraft(
        Object.fromEntries(
          list.map((c) => [c.child_player_id, c.medical_notes || ""])
        )
      );
      const rel = list.find((c) => c.relationship)?.relationship;
      if (rel) {
        if (typeof onRelationshipDetected === "function") {
          onRelationshipDetected(rel);
        }
        try {
          const stored = JSON.parse(localStorage.getItem("user") || "{}");
          if (stored && stored.guardian_relationship !== rel) {
            const next = { ...stored, guardian_relationship: rel };
            localStorage.setItem("user", JSON.stringify(next));
          }
        } catch {
          /* ignore */
        }
      }
    } catch (err) {
      console.warn("ParentProfilePanel: children load failed", err.message);
      setChildren([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadChildren();
  }, [user?.user_id]);

  const openChildLogin = async (child) => {
    const email = (emailDraft[child.child_player_id] || "").trim().toLowerCase();
    if (!email || !email.includes("@")) {
      setFeedback({
        type: "error",
        text: t("parentOpenAccountEmailRequired") || "Enter the child's personal email.",
      });
      return;
    }

    setOpeningId(child.child_player_id);
    setFeedback(null);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(
        getApiUrl("/api/parent/enable-child-login"),
        { playerId: child.child_player_id, email },
        { headers }
      );
      if (res.data?.success) {
        setFeedback({
          type: "ok",
          text:
            t("parentOpenAccountSuccess", { name: child.first_name }) ||
            `Player account opened for ${child.first_name}. A confirmation email was sent so they can set their own password.`,
        });
        setEmailDraft((prev) => ({ ...prev, [child.child_player_id]: "" }));
        await loadChildren();
      } else {
        setFeedback({
          type: "error",
          text: res.data?.message || t("parentOpenAccountFailed") || "Could not open account.",
        });
      }
    } catch (err) {
      setFeedback({
        type: "error",
        text:
          err.response?.data?.message ||
          err.message ||
          t("parentOpenAccountFailed") ||
          "Could not open account.",
      });
    } finally {
      setOpeningId(null);
    }
  };

  const uploadChildPhoto = async (child, file) => {
    if (!child?.child_player_id || !file) return;
    setPhotoBusyId(child.child_player_id);
    setFeedback(null);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const form = new FormData();
      form.append("user_image", file);
      form.append("childData", JSON.stringify({}));
      const res = await axios.put(
        getApiUrl(`/api/parent/child/${child.child_player_id}`),
        form,
        { headers }
      );
      const imageUrl = res.data?.child?.user_image || null;
      if (imageUrl) {
        setChildren((prev) =>
          prev.map((c) =>
            c.child_player_id === child.child_player_id
              ? { ...c, user_image: imageUrl }
              : c
          )
        );
        setFeedback({
          type: "ok",
          text:
            t("parentHubPhotoUpdated", { name: child.first_name }) ||
            `Photo updated for ${child.first_name}.`,
        });
      }
    } catch (err) {
      setFeedback({
        type: "error",
        text:
          err.response?.data?.message ||
          err.message ||
          t("parentHubPhotoUploadFailed") ||
          "Could not upload photo.",
      });
    } finally {
      setPhotoBusyId(null);
      const input = photoInputRefs.current[child.child_player_id];
      if (input) input.value = "";
    }
  };

  const setChildConsent = async (child, patch) => {
    if (!child?.child_player_id) return;
    const busyKey = `${child.child_player_id}:${Object.keys(patch)[0]}`;
    setConsentBusyKey(busyKey);
    setFeedback(null);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.put(
        getApiUrl(`/api/parent/child/${child.child_player_id}/consent`),
        patch,
        { headers }
      );
      const consent = res.data?.consent || {};
      setChildren((prev) =>
        prev.map((c) =>
          c.child_player_id === child.child_player_id
            ? {
                ...c,
                consent_video_analysis:
                  consent.consent_video_analysis ?? c.consent_video_analysis,
                consent_public_image:
                  consent.consent_public_image ?? c.consent_public_image,
                consent_hr_data: consent.consent_hr_data ?? c.consent_hr_data,
              }
            : c
        )
      );
      setFeedback({
        type: "ok",
        text:
          t("parentConsentUpdated", { name: child.first_name }) ||
          `Consent updated for ${child.first_name}.`,
      });
    } catch (err) {
      setFeedback({
        type: "error",
        text:
          err.response?.data?.message ||
          err.message ||
          t("parentConsentUpdateFailed") ||
          "Could not update consent.",
      });
    } finally {
      setConsentBusyKey(null);
    }
  };

  const saveMedicalNotes = async (child) => {
    if (!child?.child_player_id) return;
    setMedicalBusyId(child.child_player_id);
    setFeedback(null);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const notes = String(medicalDraft[child.child_player_id] ?? "").trim();
      const res = await axios.put(
        getApiUrl(`/api/parent/child/${child.child_player_id}`),
        { childData: { medicalNotes: notes } },
        { headers }
      );
      const saved =
        res.data?.child?.medical_notes != null
          ? res.data.child.medical_notes
          : notes || null;
      setChildren((prev) =>
        prev.map((c) =>
          c.child_player_id === child.child_player_id
            ? { ...c, medical_notes: saved }
            : c
        )
      );
      setMedicalDraft((prev) => ({
        ...prev,
        [child.child_player_id]: saved || "",
      }));
      setFeedback({
        type: "ok",
        text:
          t("parentHubMedicalNotesSaved") ||
          `Medical notes saved for ${child.first_name}.`,
      });
    } catch (err) {
      setFeedback({
        type: "error",
        text:
          err.response?.data?.message ||
          err.message ||
          t("parentHubMedicalNotesSaveFailed") ||
          "Could not save medical notes.",
      });
    } finally {
      setMedicalBusyId(null);
    }
  };

  const youthBands =
    scforhSeed?.targetGroups?.length > 0
      ? scforhSeed.targetGroups.filter((g) =>
          ["children", "adolescents", "youth"].includes(String(g).toLowerCase())
        )
      : ["children", "adolescents"];

  return (
    <div className="parent-profile-panel">
      <div className="parent-profile-scforh club-health-card">
        <div className="ios-objectives-header">
          <span>💚</span>
          <h3>{t("parentClubHealthTitle") || "Health at the club"}</h3>
        </div>
        {isScforhSeedComplete(scforhSeed) ? (
          <p className="parent-profile-scforh-note">
            {t("parentClubHealthNote") ||
              "Your club follows Sports Club for Health principles — physical, mental, and social well-being woven into training. Pingwi helps younger players stay joyful and active."}
          </p>
        ) : (
          <p className="parent-profile-scforh-note">
            {t("parentClubHealthNoteGeneric") ||
              "Table tennis supports physical, mental, and social health. As a parent you can follow progress while Pingwi guides younger players at the club."}
          </p>
        )}
        <details className="parent-profile-scforh-expand">
          <summary>
            {t("whoCapsuleEyebrow") || "WHO · Sports Club for Health"}
          </summary>
          <ScforhWhoCapsule
            targetGroups={
              youthBands.length
                ? youthBands
                : ["children", "adolescents"]
            }
            variant="compact"
          />
        </details>
      </div>

      <div className="parent-profile-children">
        <div className="parent-profile-children-header">
          <h3>{t("parentLinkedChildren") || "Your children"}</h3>
          <button
            type="button"
            className="parent-profile-link-btn"
            onClick={() => navigate("/parent/progress")}
          >
            {t("parentOpenProgressHub") || "Progress Hub"}
          </button>
        </div>

        {loading ? (
          <p className="parent-profile-muted">{t("loading") || "Loading…"}</p>
        ) : !children.length ? (
          <div className="parent-profile-empty">
            <p>{t("parentNoChildrenYet") || "No children linked yet."}</p>
            <button
              type="button"
              className="parent-profile-primary-btn"
              onClick={() => navigate("/parent-profiling")}
            >
              {t("parentAddChild") || "Add a child"}
            </button>
          </div>
        ) : (
          <ul className="parent-profile-child-list">
            {children.map((child) => (
              <li key={child.child_player_id} className="parent-profile-child-card">
                <div className="parent-profile-child-top">
                  <button
                    type="button"
                    className="parent-profile-child-avatar parent-profile-child-avatar--photo"
                    disabled={photoBusyId === child.child_player_id}
                    onClick={() =>
                      photoInputRefs.current[child.child_player_id]?.click()
                    }
                    title={
                      t("parentHubUploadPhoto") || "Upload profile picture"
                    }
                    aria-label={
                      t("parentHubUploadPhoto") || "Upload profile picture"
                    }
                  >
                    {resolveChildImageUrl(child.user_image) ? (
                      <img
                        src={resolveChildImageUrl(child.user_image)}
                        alt=""
                        className="parent-profile-child-avatar-img"
                      />
                    ) : (
                      <AgentMark
                        agentId={child.companion?.agentId || "pingwi"}
                        size={36}
                      />
                    )}
                  </button>
                  <div>
                    <strong>
                      {child.first_name}
                      {child.age != null ? ` · ${child.age} yrs` : ""}
                    </strong>
                    <p>
                      {child.companion?.displayName || "Pingwi"}
                      {child.has_own_login
                        ? ` · ${t("parentChildHasLogin") || "Has player login"}`
                        : ""}
                    </p>
                    <button
                      type="button"
                      className="parent-profile-photo-btn"
                      disabled={photoBusyId === child.child_player_id}
                      onClick={() =>
                        photoInputRefs.current[child.child_player_id]?.click()
                      }
                    >
                      {photoBusyId === child.child_player_id
                        ? t("parentHubUploadingPhoto") || "Uploading…"
                        : resolveChildImageUrl(child.user_image)
                          ? t("parentHubChangePhoto") || "Change photo"
                          : t("parentHubUploadPhoto") || "Upload profile picture"}
                    </button>
                    {/* No capture attr: opens gallery/files on mobile (camera remains available via picker). */}
                    <input
                      ref={(el) => {
                        photoInputRefs.current[child.child_player_id] = el;
                      }}
                      type="file"
                      accept="image/*,.heic,.heif,.jpeg,.jpg,.png,.webp"
                      hidden
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) uploadChildPhoto(child, file);
                      }}
                    />
                  </div>
                </div>

                {flttClub && child.child_user_id ? (
                  <div className="parent-profile-fltt">
                    <p className="parent-profile-muted">
                      Prepare the FLTT form for {child.first_name}. Your name is already the tutor. Fill anything still missing, then print it to sign.
                    </p>
                    <button
                      type="button"
                      className="parent-profile-secondary-btn"
                      onClick={() => setFlttChild(child)}
                    >
                      FLTT form to sign
                    </button>
                  </div>
                ) : null}

                <div className="parent-profile-consent" data-testid="parent-consent">
                  <div className="parent-profile-consent-head">
                    <p className="parent-profile-consent-title">
                      {t("parentConsentTitle") || "Training video, public image and heart rate"}
                    </p>
                    <p className="parent-profile-consent-kicker">
                      {t("parentConsentStatusKicker", {
                        name: child.first_name,
                      }) || `Current consent for ${child.first_name}`}
                    </p>
                  </div>
                  <div className="parent-profile-consent-grid">
                    <div
                      className={`parent-profile-consent-tile ${
                        child.consent_video_analysis
                          ? "is-granted"
                          : "is-missing"
                      }`}
                    >
                      <span className="parent-profile-consent-kind">
                        {t("consentTrainingShort") || "Video"}
                      </span>
                      <strong className="parent-profile-consent-state">
                        {child.consent_video_analysis
                          ? t("parentConsentGranted") || "Granted"
                          : t("parentConsentNotGranted") || "Not granted"}
                      </strong>
                      <span className="parent-profile-consent-detail">
                        {t("consentTraining") ||
                          "Training video and movement analysis"}
                      </span>
                      <button
                        type="button"
                        className={`parent-profile-consent-btn ${
                          child.consent_video_analysis ? "is-on" : ""
                        }`}
                        disabled={
                          consentBusyKey ===
                          `${child.child_player_id}:consentVideo`
                        }
                        onClick={() =>
                          setChildConsent(child, {
                            consentVideo: !child.consent_video_analysis,
                          })
                        }
                      >
                        {child.consent_video_analysis
                          ? t("consentWithdraw") || "Withdraw"
                          : t("consentGrant") || "I consent"}
                      </button>
                    </div>
                    <div
                      className={`parent-profile-consent-tile ${
                        child.consent_hr_data ? "is-granted" : "is-missing"
                      }`}
                    >
                      <span className="parent-profile-consent-kind">
                        {t("consentHeartRateShort") || "Heart rate"}
                      </span>
                      <strong className="parent-profile-consent-state">
                        {child.consent_hr_data
                          ? t("parentConsentGranted") || "Granted"
                          : t("parentConsentNotGranted") || "Not granted"}
                      </strong>
                      <span className="parent-profile-consent-detail">
                        {t("consentHeartRate") ||
                          "Heart-rate measurement (health data)"}
                      </span>
                      <button
                        type="button"
                        className={`parent-profile-consent-btn ${
                          child.consent_hr_data ? "is-on" : ""
                        }`}
                        disabled={
                          consentBusyKey === `${child.child_player_id}:consentHr`
                        }
                        onClick={() =>
                          setChildConsent(child, {
                            consentHr: !child.consent_hr_data,
                          })
                        }
                      >
                        {child.consent_hr_data
                          ? t("consentWithdraw") || "Withdraw"
                          : t("consentGrant") || "I consent"}
                      </button>
                    </div>
                    <div
                      className={`parent-profile-consent-tile ${
                        child.consent_public_image ? "is-granted" : "is-missing"
                      }`}
                    >
                      <span className="parent-profile-consent-kind">
                        {t("consentPublicShort") || "Public"}
                      </span>
                      <strong className="parent-profile-consent-state">
                        {child.consent_public_image
                          ? t("parentConsentGranted") || "Granted"
                          : t("parentConsentNotGranted") || "Not granted"}
                      </strong>
                      <span className="parent-profile-consent-detail">
                        {t("consentPublic") || "Public / social use"}
                      </span>
                      <button
                        type="button"
                        className={`parent-profile-consent-btn ${
                          child.consent_public_image ? "is-on" : ""
                        }`}
                        disabled={
                          consentBusyKey ===
                          `${child.child_player_id}:consentPublic`
                        }
                        onClick={() =>
                          setChildConsent(child, {
                            consentPublic: !child.consent_public_image,
                          })
                        }
                      >
                        {child.consent_public_image
                          ? t("consentWithdraw") || "Withdraw"
                          : t("consentGrant") || "I consent"}
                      </button>
                    </div>
                  </div>
                  <p className="parent-profile-muted parent-profile-consent-help">
                    {t("parentConsentHelp") ||
                      "Each purpose is optional. One parent or guardian can grant or withdraw here. A paper form is only a fallback. Training continues either way."}
                  </p>
                </div>

                <div className="parent-profile-medical">
                  <p className="parent-profile-consent-title">
                    {t("parentHubMedicalNotesTitle") || "Medical notes"}
                  </p>
                  <textarea
                    className="parent-profile-medical-input"
                    rows={3}
                    value={
                      medicalDraft[child.child_player_id] ??
                      child.medical_notes ??
                      ""
                    }
                    placeholder={
                      t("parentWizardMedicalNotesPlaceholder") ||
                      "Optional: Medical notes (e.g. asthma, allergies)"
                    }
                    onChange={(e) =>
                      setMedicalDraft((prev) => ({
                        ...prev,
                        [child.child_player_id]: e.target.value,
                      }))
                    }
                  />
                  <button
                    type="button"
                    className="parent-profile-secondary-btn"
                    disabled={
                      medicalBusyId === child.child_player_id ||
                      String(
                        medicalDraft[child.child_player_id] ??
                          child.medical_notes ??
                          ""
                      ).trim() === String(child.medical_notes || "").trim()
                    }
                    onClick={() => saveMedicalNotes(child)}
                  >
                    {medicalBusyId === child.child_player_id
                      ? t("parentWizardSaving") || "Saving..."
                      : t("parentHubMedicalNotesSave") || "Save medical notes"}
                  </button>
                </div>

                {child.can_open_player_account ? (
                  <div className="parent-profile-open-account">
                    <p>
                      {t("parentOpenAccountHint", {
                        age: child.min_self_login_age || 13,
                        name: child.first_name,
                      }) ||
                        `${child.first_name} is old enough for their own player account. You can both follow progress with Pingwi.`}
                    </p>
                    <div className="parent-profile-open-row">
                      <input
                        type="email"
                        placeholder={
                          t("parentOpenAccountEmailPlaceholder") ||
                          "Child's personal email"
                        }
                        value={emailDraft[child.child_player_id] || ""}
                        onChange={(e) =>
                          setEmailDraft((prev) => ({
                            ...prev,
                            [child.child_player_id]: e.target.value,
                          }))
                        }
                        disabled={openingId === child.child_player_id}
                      />
                      <button
                        type="button"
                        className="parent-profile-primary-btn"
                        disabled={openingId === child.child_player_id}
                        onClick={() => openChildLogin(child)}
                      >
                        {openingId === child.child_player_id
                          ? t("sending") || "Sending…"
                          : t("parentOpenAccountCta") || "Open player account"}
                      </button>
                    </div>
                  </div>
                ) : !child.has_own_login && child.age != null ? (
                  <p className="parent-profile-muted">
                    {t("parentOpenAccountTooYoung", {
                      age: child.min_self_login_age || 13,
                    }) ||
                      `A personal player login becomes available from age ${
                        child.min_self_login_age || 13
                      }. Until then, follow progress in the Hub with Pingwi.`}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {children.length > 0 ? (
          <button
            type="button"
            className="parent-profile-secondary-btn"
            onClick={() => navigate("/parent-profiling")}
          >
            {t("parentAddChild") || "Add another child"}
          </button>
        ) : null}

        {feedback ? (
          <p
            className={`parent-profile-feedback parent-profile-feedback--${feedback.type}`}
            role="status"
          >
            {feedback.text}
          </p>
        ) : null}
      </div>
      <FlttFormDialog
        open={Boolean(flttChild)}
        userId={flttChild?.child_user_id}
        clubId={flttChild?.club_id}
        onClose={() => setFlttChild(null)}
      />
    </div>
  );
}
